import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DrawerV2 } from '@withjoy/joykit';
import { ChevronDown, ChevronRight } from '@withjoy/joykit/icons';
import { useHistory, useTranslation } from '@shared/core';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { ColumnHeading } from './CategoryMegaNav.styles';
import { MegaNavLink } from './MegaNavLink';
import { SheetAllLink, SheetBody, SheetList, SheetSection, SheetTileGrid, Strip, StripChip, StripItem, StripList } from './CategoryMegaNavMobile.styles';
import type { MegaNavLinkClickHandler } from './MegaNavPanel';
import type { MegaNavSlotId } from './megaNav.config';
import type { ResolvedMegaNavSlot } from './useResolvedMegaNav';
import { HolidayLights } from './HolidayLights';
import { useIsHolidaySeason } from './holidaySeason';

/** Which nav surface a category view came from; the event name is shared across breakpoints. */
export type MegaNavSurface = 'megaNav' | 'mobileSheet';

type CategoryMegaNavMobileProps = Readonly<{
  slots: ReadonlyArray<ResolvedMegaNavSlot>;
  onLinkClick: MegaNavLinkClickHandler;
  /** The slot's own destination was chosen — same event as a desktop slot click, tagged with this surface. */
  onCategoryView: (slot: ResolvedMegaNavSlot, source: MegaNavSurface) => void;
}>;

/**
 * Mobile counterpart of the mega nav: a horizontal strip of the five slots below the
 * header, each opening a bottom sheet with that slot's sections. Same resolved data as
 * the desktop panel, so links and telemetry stay identical across breakpoints.
 */
export const CategoryMegaNavMobile = ({ slots, onLinkClick, onCategoryView }: CategoryMegaNavMobileProps) => {
  const { tq } = useTranslation('stationery');
  const { buttonInteracted } = useCardTelemetry();
  const history = useHistory();
  const isHolidaySeason = useIsHolidaySeason();
  const [openSlotId, setOpenSlotId] = useState<MegaNavSlotId | null>(null);
  const openSlot = slots.find(slot => slot.id === openSlotId);
  // Keep the last opened slot around so the sheet's content stays put while
  // `DrawerV2` plays its exit animation; `isOpen` alone drives visibility. The ref is
  // updated after render (not during), so the render that closes the sheet still sees
  // the previous slot.
  const lastOpenSlotRef = useRef<ResolvedMegaNavSlot | undefined>(undefined);
  useEffect(() => {
    if (openSlot) lastOpenSlotRef.current = openSlot;
  }, [openSlot]);
  const sheetSlot = openSlot ?? lastOpenSlotRef.current;

  const close = useCallback(() => setOpenSlotId(null), []);

  useEffect(() => history.listen(close), [history, close]);

  const openSheet = (slot: ResolvedMegaNavSlot) => {
    buttonInteracted({ name: 'categoryNav_megaNavOpened', slotId: slot.id, trigger: 'tap', source: 'mobileSheet' });
    setOpenSlotId(slot.id);
  };

  const handleLinkClick: MegaNavLinkClickHandler = (link, context) => {
    onLinkClick(link, context);
    close();
  };

  const handleCategoryView = (slot: ResolvedMegaNavSlot) => {
    onCategoryView(slot, 'mobileSheet');
    close();
  };

  return (
    <>
      <Strip
        aria-label={tq`Category navigation`}
        data-testid="category-mega-nav-mobile"
      >
        <StripList data-testid="category-mega-nav-mobile-strip">
          {slots.map(slot => (
            <StripItem key={slot.id}>
              <StripChip
                type="button"
                $isActive={slot.isActive}
                aria-current={slot.isActive ? 'true' : undefined}
                aria-haspopup="dialog"
                aria-expanded={openSlotId === slot.id}
                data-testid={`category-mega-nav-mobile-slot-${slot.id}`}
                onClick={() => openSheet(slot)}
              >
                {slot.label}
                <ChevronDown
                  size="sm"
                  aria-hidden="true"
                />
                {slot.id === 'holiday' && isHolidaySeason && <HolidayLights placement="strip" />}
              </StripChip>
            </StripItem>
          ))}
        </StripList>
      </Strip>
      <DrawerV2
        anchor="bottom"
        size="auto"
        isOpen={!!openSlot}
        onClose={close}
        overrides={{
          // `DrawerV2` always labels its dialog with the generic translated "Drawer"; the
          // supported way to name it is the Content override (see Drawer.tsx).
          Content: { props: { 'aria-label': sheetSlot?.label, borderTopLeftRadius: 3, borderTopRightRadius: 3, maxHeight: '85vh' } },
          Body: { props: { paddingX: 0, paddingY: 0, overflowY: 'auto' } },
        }}
      >
        {sheetSlot && (
          <>
            <DrawerV2.Header
              typographyVariant="hed5"
              fontWeight={600}
            >
              {sheetSlot.label}
              <DrawerV2.CloseButton />
            </DrawerV2.Header>
            <DrawerV2.Body>
              <SheetBody data-testid={`category-mega-nav-mobile-sheet-${sheetSlot.id}`}>
                {sheetSlot.link && sheetSlot.allLabel && (
                  <SheetAllLink>
                    <MegaNavLink
                      link={sheetSlot.link}
                      onClick={() => handleCategoryView(sheetSlot)}
                    >
                      {sheetSlot.allLabel}
                      <ChevronRight
                        size="sm"
                        aria-hidden="true"
                      />
                    </MegaNavLink>
                  </SheetAllLink>
                )}
                {sheetSlot.tiles && sheetSlot.tiles.length > 0 && (
                  <SheetTileGrid $columns={sheetSlot.tiles.length === 3 ? 3 : 2}>
                    {sheetSlot.tiles.map(tile => (
                      <MegaNavLink
                        key={tile.id}
                        link={tile}
                        variant="tile"
                        onClick={() => handleLinkClick(tile, { slot: sheetSlot, variant: 'tile' })}
                      >
                        <img
                          src={tile.imageSrc}
                          alt=""
                          loading="lazy"
                        />
                        <span>{tile.label}</span>
                      </MegaNavLink>
                    ))}
                  </SheetTileGrid>
                )}
                {sheetSlot.columns.map(column => (
                  <SheetSection
                    key={column.id}
                    aria-label={column.heading}
                    data-testid={`category-mega-nav-mobile-section-${column.id}`}
                  >
                    <ColumnHeading>{column.heading}</ColumnHeading>
                    <SheetList>
                      {column.links.map(link => (
                        <li key={link.id}>
                          <MegaNavLink
                            link={link}
                            onClick={() => handleLinkClick(link, { slot: sheetSlot, column, variant: 'text' })}
                          >
                            {link.label}
                          </MegaNavLink>
                        </li>
                      ))}
                    </SheetList>
                  </SheetSection>
                ))}
              </SheetBody>
            </DrawerV2.Body>
          </>
        )}
      </DrawerV2>
    </>
  );
};
